/**
 * The stats overlay for the volunteer device test (ADR 0028 amendment,
 * 2026-09-28): a small panel with what the renderer measures on this device
 * and a button that copies the same numbers as a JSON block, for a volunteer
 * to paste into a report. The shell shows it with `?stats=1`. It reads a
 * report through `read` (the shell builds it from the view's stats); the
 * component computes nothing itself. Since slice 6b it sits in a `corner`
 * panel and the pause menu's stats row shows or hides it.
 */
import { useEffect, useRef, useState } from 'react';
import { type Strings, locales } from '@ab/session';
import { languageKey } from './language.ts';
import { Panel } from './panel.tsx';
import { text } from './text.ts';
import './ui.css';

export type StatsReport = {
  readonly tier: string;
  readonly pixelRatio: number;
  readonly canvas: readonly [number, number];
  readonly drawCalls: number;
  readonly triangles: number;
  /** p90 of what the last 120 frames cost the CPU to submit (all passes), ms: the frame's cost, whatever paced it. */
  readonly cpuFrameP90Ms: number | null;
  /** p90 of the last 120 measured GPU frame times, ms; null where the timer query is missing (iOS Safari). */
  readonly gpuFrameP90Ms: number | null;
  /** p90 of the intervals between the last 120 back-to-back animation frames, ms: the frame rate while something moves (idle gaps excluded); null before any animation. */
  readonly animationFrameP90Ms: number | null;
  /** `performance.memory.usedJSHeapSize` in MB where the browser has it. */
  readonly heapMb: number | null;
  readonly devicePixelRatio: number;
  readonly userAgent: string;
  /** The walkable scene shown (slice 6a: `scene.la_tirana`); absent in the dungeon, so its report keeps its keys. */
  readonly scene?: string;
  /** A scene's time of day, `day` or `night` (ADR 0032); absent in the dungeon. */
  readonly time?: string;
  /** The page's locale, the visual viewport's size in CSS pixels and whether the touch pad shows (slice 6b, the volunteer recipe). */
  readonly locale: string;
  readonly viewport: readonly [number, number];
  readonly pad: boolean;
};

export type StatsPanelProps = {
  readonly strings: Strings;
  readonly read: () => StatsReport;
  /** How often the panel refreshes, ms. */
  readonly everyMs: number;
  readonly copy: (json: string) => Promise<void>;
};

const shown = (strings: Strings, v: number | null, digits: number): string => (v === null ? text(strings, 'ui.stats.none') : v.toFixed(digits));

/** A locale's own name (`ui.lang.*`), or the code itself for one the game does not have. */
const languageName = (strings: Strings, code: string): string => {
  const locale = locales.find((l) => l === code);
  return locale === undefined ? code : text(strings, languageKey(locale));
};

export const StatsPanel = ({ strings, read, everyMs, copy }: StatsPanelProps) => {
  const [report, setReport] = useState<StatsReport>(read);
  const [copied, setCopied] = useState(false);
  /** The report shown for copying by hand when the clipboard is not available (an insecure page, a refusal). */
  const [manual, setManual] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    area.current?.select();
  }, [manual]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setReport(read());
    }, everyMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [read, everyMs]);
  const rows: readonly (readonly [string, string])[] = [
    // The tier's word in the page's language (the copied JSON keeps the id).
    ['ui.stats.tier', text(strings, `ui.status.tier_${report.tier}`)],
    // The scene is an id, as the copied report keeps it (a developer's row, like the user agent).
    ...(report.scene === undefined ? [] : [['ui.stats.scene', report.scene] as const]),
    ['ui.stats.pixel_ratio', report.pixelRatio.toFixed(2)],
    ['ui.stats.canvas', `${report.canvas[0]}×${report.canvas[1]}`],
    ['ui.stats.draws', String(report.drawCalls)],
    ['ui.stats.triangles', String(report.triangles)],
    ['ui.stats.cpu_p90', shown(strings, report.cpuFrameP90Ms, 2)],
    ['ui.stats.gpu_p90', shown(strings, report.gpuFrameP90Ms, 2)],
    ['ui.stats.animation_p90', shown(strings, report.animationFrameP90Ms, 1)],
    ['ui.stats.heap', shown(strings, report.heapMb, 1)],
    ['ui.stats.device_pixel_ratio', report.devicePixelRatio.toFixed(2)],
    // The language's own name (`ui.lang.*`, as the pause menu shows it); the copied JSON keeps the code.
    ['ui.stats.locale', languageName(strings, report.locale)],
    ['ui.stats.viewport', `${report.viewport[0]}×${report.viewport[1]}`],
    ['ui.stats.pad', text(strings, report.pad ? 'ui.menu.on' : 'ui.menu.off')],
    ['ui.stats.user_agent', report.userAgent],
  ];
  return (
    <Panel placement="corner" className="ab-stats" data-testid="stats-panel" aria-label={text(strings, 'ui.stats.title')}>
      <dl>
        {rows.map(([key, value]) => (
          <div key={key}>
            <dt>{text(strings, key)}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <button
        type="button"
        onClick={() => {
          const json = JSON.stringify(read(), null, 2);
          const fallback = (): void => {
            setManual(json);
          };
          try {
            copy(json).then(() => {
              setCopied(true);
            }, fallback);
          } catch {
            fallback();
          }
        }}
      >
        {text(strings, copied ? 'ui.stats.copied' : 'ui.stats.copy')}
      </button>
      {manual !== null && (
        <>
          <p>{text(strings, 'ui.stats.copy_by_hand')}</p>
          <textarea ref={area} readOnly value={manual} rows={8} data-testid="stats-report" />
        </>
      )}
    </Panel>
  );
};
